import { useEffect, useState } from "react";
import { MapPin, Plus, Trash2, X } from "lucide-react";
import { Button, Modal, Select, PhoneInput } from "../../../components";
import DeliveryLocationPicker from "../../../components/DeliveryLocationPicker/DeliveryLocationPicker";
import "./CustomerModal.scss";
import { isValidDeliveryLocation } from "../../../features/orderUtils";

const PAYMENT_OPTIONS = [
  { value: "", label: "Bez predvolenej platby" },
  { value: "CASH", label: "Hotovosť" },
  { value: "CARD", label: "Karta" },
  { value: "INVOICE", label: "Faktúra" },
  { value: "DEBT", label: "Dlh" },
];
const DEFAULT_PRICE_LIST_CODE = "STANDARD";

function splitPhone(rawPhone) {
  const value = String(rawPhone || "");
  const prefixes = ["+421", "+420"];
  const dialCode = prefixes.find((prefix) => value.startsWith(prefix)) || "+421";
  const localPart = value.startsWith(dialCode) ? value.slice(dialCode.length) : value;
  return { dialCode, localPart };
}

function createEmptyAddress() {
  return {
    id: null,
    address: "",
    address_code: "",
    address_note: "",
    // Pridelená rozvozová trasa (R54). Prázdna = zákazník si cez web objedná
    // len s vyzdvihnutím na prevádzke.
    route_id: "",
    default_payment_type: "",
    latitude: null,
    longitude: null,
  };
}

function hasAddressOrCode(address, addressCode) {
  return String(address || "").trim().length > 0 || String(addressCode || "").trim().length > 0;
}

function CustomerModal({ isOpen, onClose, customer, onSubmit, isSubmitting, priceLists = [], routes = [] }) {
  const [phoneDialCode, setPhoneDialCode] = useState("+421");
  const [phoneLocal, setPhoneLocal] = useState("");
  const [form, setForm] = useState({
    surname: "",
    name: "",
    email: "",
    phone_number: "",
    kod_cennik: DEFAULT_PRICE_LIST_CODE,
    addresses: [createEmptyAddress()],
  });
  const [addressErrors, setAddressErrors] = useState({});
  const [locationPickerIndex, setLocationPickerIndex] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!customer) {
      setPhoneDialCode("+421");
      setPhoneLocal("");
      setForm({
        surname: "",
        name: "",
        email: "",
        phone_number: "",
        kod_cennik: DEFAULT_PRICE_LIST_CODE,
        addresses: [createEmptyAddress()],
      });
      setAddressErrors({});
      setLocationPickerIndex(null);
      return;
    }

    const { dialCode, localPart } = splitPhone(customer.phone_number || "");
    const mappedAddresses = Array.isArray(customer.addresses) && customer.addresses.length > 0
      ? customer.addresses.map((address) => {
          return {
            id: address.id || null,
            address: address.address || "",
            address_code: address.address_code || "",
            address_note: address.address_note || "",
            route_id: address.route_id ? String(address.route_id) : "",
            default_payment_type: address.default_payment_type || "",
            latitude: address.latitude ?? null,
            longitude: address.longitude ?? null,
          };
        })
      : [createEmptyAddress()];

    setPhoneDialCode(dialCode);
    setPhoneLocal(localPart);
    setForm({
      surname: customer.surname || "",
      name: customer.name || "",
      email: customer.email || "",
      phone_number: customer.phone_number || "",
      kod_cennik: customer.kod_cennik || DEFAULT_PRICE_LIST_CODE,
      addresses: mappedAddresses,
    });
    setAddressErrors({});
    setLocationPickerIndex(null);
  }, [isOpen, customer]);

  const updateAddress = (index, key, value) => {
    setForm((prev) => {
      const next = [...prev.addresses];
      next[index] = { ...next[index], [key]: value };
      return { ...prev, addresses: next };
    });

    if (key === "address" || key === "address_code") {
      setAddressErrors((prev) => {
        if (!prev[index]) {
          return prev;
        }
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  const addAddress = () => {
    setForm((prev) => ({
      ...prev,
      addresses: [...prev.addresses, createEmptyAddress()],
    }));
  };

  const removeAddress = (index) => {
    setForm((prev) => {
      if (prev.addresses.length === 1) {
        return prev;
      }
      return {
        ...prev,
        addresses: prev.addresses.filter((_, rowIndex) => rowIndex !== index),
      };
    });
    setAddressErrors({});
    setLocationPickerIndex(null);
  };

  const handleLocationConfirm = (nextLocation) => {
    if (locationPickerIndex === null) return;
    updateAddress(locationPickerIndex, "latitude", nextLocation?.lat ?? null);
    updateAddress(locationPickerIndex, "longitude", nextLocation?.lng ?? null);
  };

  const activeLocationAddress = locationPickerIndex !== null ? form.addresses[locationPickerIndex] : null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nextAddressErrors = {};
    const addresses = [];

    form.addresses.forEach((row, index) => {
      const normalizedAddress = String(row.address || "").trim();
      const normalizedCode = String(row.address_code || "").trim().toUpperCase();

      if (!hasAddressOrCode(normalizedAddress, normalizedCode)) {
        nextAddressErrors[index] = true;
        return;
      }

      addresses.push({
        id: row.id || null,
        address: normalizedAddress || null,
        address_code: normalizedCode || null,
        address_note: String(row.address_note || "").trim() || null,
        route_id: row.route_id ? Number(row.route_id) : null,
        default_payment_type: row.default_payment_type || null,
        latitude: isValidDeliveryLocation({ lat: row.latitude, lng: row.longitude }) ? row.latitude : null,
        longitude: isValidDeliveryLocation({ lat: row.latitude, lng: row.longitude }) ? row.longitude : null,
      });
    });

    if (addresses.length === 0) {
      setAddressErrors(nextAddressErrors);
      return;
    }

    setAddressErrors({});

    const payload = {
      surname: form.surname.trim(),
      name: form.name.trim(),
      email: form.email.trim(),
      phone_number: form.phone_number.trim(),
      kod_cennik: form.kod_cennik || DEFAULT_PRICE_LIST_CODE,
      addresses,
    };

    await onSubmit(payload);
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={customer ? "Upraviť zákazníka" : "Pridať zákazníka"}
      size="xlarge"
    >
      <form className="customer-modal__form" onSubmit={handleSubmit}>
        <div className="customer-modal__row">
          <div className="customer-modal__field">
            <label className="customer-modal__label">Priezvisko</label>
            <input
              className="customer-modal__input"
              value={form.surname}
              maxLength={200}
              onChange={(e) => setForm((prev) => ({ ...prev, surname: e.target.value }))}
              placeholder="Novák"
            />
          </div>
          <div className="customer-modal__field">
            <label className="customer-modal__label">Meno</label>
            <input
              className="customer-modal__input"
              value={form.name}
              maxLength={200}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Ján"
            />
          </div>
        </div>

        <div className="customer-modal__row">
          <div className="customer-modal__field">
            <label className="customer-modal__label">E-mail</label>
            <input
              className="customer-modal__input"
              type="email"
              maxLength={255}
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              placeholder="zakaznik@example.com"
            />
          </div>

          <div className="customer-modal__field">
            <label className="customer-modal__label">Telefón</label>
            <PhoneInput
              value={phoneLocal}
              prefix={phoneDialCode}
              onChange={(localValue) => {
                setPhoneLocal(localValue);
                setForm((prev) => ({ ...prev, phone_number: phoneDialCode + localValue }));
              }}
              onPrefixChange={(dialCode) => {
                setPhoneDialCode(dialCode);
                setForm((prev) => ({ ...prev, phone_number: dialCode + phoneLocal }));
              }}
            />
          </div>
        </div>

        {/* Predvolená platba je vlastnosťou ADRESY, nie zákazníka — na každú
            sa môže voziť inak a server ju aj tak ukladá len pri adrese.
            Druhé pole tu rovnakú vec pýtalo druhý raz a nikam sa neukladalo. */}
        <div className="customer-modal__row">
          <Select
            label="Cenník"
            options={priceLists.map((priceList) => ({
              value: priceList.kod,
              label: priceList.popis ? `${priceList.kod} - ${priceList.popis}` : priceList.kod,
            }))}
            value={form.kod_cennik}
            onChange={(value) => setForm((prev) => ({ ...prev, kod_cennik: value || "" }))}
          />
        </div>

        <div className="customer-modal__addresses-head">
          <h3 className="customer-modal__addresses-title">Adresy zákazníka</h3>
          <Button variant="ghost" type="button" onClick={addAddress}>
            <Plus size={14} /> Pridať adresu
          </Button>
        </div>

        <div className="customer-modal__addresses-list">
          {form.addresses.map((address, index) => (
            <div key={address.id || index} className="customer-modal__address-row">
              <div className="customer-modal__address-grid">
                <div className="customer-modal__address-main-row">
                  <div className="customer-modal__field customer-modal__field--address-code">
                    <label className="customer-modal__label">Kód adresy</label>
                    <input
                      className={`customer-modal__input${addressErrors[index] ? " customer-modal__input--invalid" : ""}`}
                      value={address.address_code}
                      maxLength={5}
                      onChange={(e) => updateAddress(index, "address_code", e.target.value)}
                      placeholder="BA101"
                    />
                  </div>
                  <div className="customer-modal__field customer-modal__field--address-text">
                    <label className="customer-modal__label">Adresa</label>
                    <input
                      className={`customer-modal__input${addressErrors[index] ? " customer-modal__input--invalid" : ""}`}
                      value={address.address}
                      maxLength={255}
                      onChange={(e) => updateAddress(index, "address", e.target.value)}
                      placeholder="Hlavná 12, Bratislava"
                    />
                  </div>
                  <button
                    type="button"
                    className="customer-modal__remove-btn"
                    onClick={() => removeAddress(index)}
                    disabled={form.addresses.length === 1}
                    title="Odstrániť adresu"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="customer-modal__field">
                  <Select
                    label="Predvolená platba"
                    options={PAYMENT_OPTIONS}
                    value={address.default_payment_type || ""}
                    onChange={(value) => updateAddress(index, "default_payment_type", value || "")}
                  />
                </div>

                {/* Rozvozová trasa (R54). Kým ju adresa nemá, zákazník si cez web
                    objedná len s vyzdvihnutím na prevádzke — presne to je tá brána,
                    ktorú chcel majiteľ: donášku povoľuje človek. */}
                <div className="customer-modal__field">
                  <Select
                    label="Rozvozová trasa"
                    options={[
                      { value: "", label: "— bez trasy (len odber na prevádzke) —" },
                      ...routes.map((r) => ({
                        value: String(r.id),
                        label: r.name,
                        color: r.color_code,
                      })),
                    ]}
                    value={address.route_id || ""}
                    onChange={(value) => updateAddress(index, "route_id", value || "")}
                  />
                  <p className="customer-modal__hint">
                    {address.route_id
                      ? "Zákazníkovi sa na túto adresu vozí — cez web si objedná donášku."
                      : "Bez trasy si zákazník cez web objedná len s vyzdvihnutím na prevádzke."}
                  </p>
                </div>

                <div className="customer-modal__field">
                  <label className="customer-modal__label">Spresnenie pre vodiča</label>
                  <input
                    className="customer-modal__input"
                    value={address.address_note || ""}
                    maxLength={100}
                    onChange={(e) => updateAddress(index, "address_note", e.target.value)}
                    placeholder="3. poschodie, zvonček Novák"
                  />
                </div>
                <div className="customer-modal__location-row">
                  {isValidDeliveryLocation({ lat: address.latitude, lng: address.longitude }) ? (
                    <>
                      <span className="customer-modal__location-tag">
                        <MapPin size={14} /> Poloha spresnená
                      </span>
                      <button
                        type="button"
                        className="customer-modal__location-action"
                        onClick={() => setLocationPickerIndex(index)}
                      >
                        Zmeniť
                      </button>
                      <button
                        type="button"
                        className="customer-modal__location-action customer-modal__location-action--remove"
                        onClick={() => {
                          updateAddress(index, "latitude", null);
                          updateAddress(index, "longitude", null);
                        }}
                        aria-label="Zrušiť polohu"
                      >
                        <X size={14} />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="customer-modal__location-button"
                      onClick={() => setLocationPickerIndex(index)}
                      disabled={!hasAddressOrCode(address.address, address.address_code)}
                    >
                      <MapPin size={15} /> Spresniť polohu na mape
                    </button>
                  )}
                </div>
              </div>

              {addressErrors[index] && (
                <p className="customer-modal__field-error">Vyplňte kód adresy alebo adresu.</p>
              )}
            </div>
          ))}
        </div>

        <div className="customer-modal__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Zrušiť
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? "Ukladám..." : customer ? "Uložiť zmeny" : "Pridať zákazníka"}
          </Button>
        </div>
      </form>
    </Modal>

    <DeliveryLocationPicker
      isOpen={locationPickerIndex !== null}
      address={activeLocationAddress?.address || ""}
      location={
        isValidDeliveryLocation({ lat: activeLocationAddress?.latitude, lng: activeLocationAddress?.longitude })
          ? { lat: activeLocationAddress.latitude, lng: activeLocationAddress.longitude }
          : null
      }
      onConfirm={handleLocationConfirm}
      onClose={() => setLocationPickerIndex(null)}
    />
    </>
  );
}

export default CustomerModal;
